import { useTheme } from "@/context/ThemeContext";
import {
  THEME_PRESETS, FONT_OPTIONS, FONT_SIZE_OPTIONS, DENSITY_OPTIONS,
} from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle, ArrowCounterClockwise, Palette, TextT, Rows } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLOR_KEYS = [
  { key: "bg", label: "Achtergrond" },
  { key: "surface", label: "Oppervlak / Kaarten" },
  { key: "surfaceAlt", label: "Verhoogd oppervlak" },
  { key: "border", label: "Rand" },
  { key: "text", label: "Primaire tekst" },
  { key: "textMuted", label: "Secundaire tekst" },
  { key: "primary", label: "Primair / Knoppen" },
  { key: "primaryHover", label: "Primair hover" },
  { key: "primaryText", label: "Knop-tekst" },
  { key: "accent", label: "Accent" },
  { key: "danger", label: "Gevaar" },
];

function ColorField({ label, value, onChange, testid }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000";
  return (
    <div className="flex items-center gap-3">
      <div className="relative shrink-0">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-lg border border-slate-800 bg-transparent cursor-pointer"
          data-testid={`${testid}-picker`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 h-8 bg-slate-900 border-slate-800 text-slate-100 font-mono text-xs"
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { settings, applyPreset, setColor, setFontFamily, setFontSize, setDensity, resetDefaults } = useTheme();

  return (
    <div className="p-5 lg:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Personaliseren</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">Weergave</h1>
        <div className="text-sm text-slate-500 mt-1">
          Maak Nova helemaal van jou. Aanpassingen zijn direct zichtbaar in de hele app.
        </div>
      </div>

      <Tabs defaultValue="presets" className="space-y-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="presets" data-testid="settings-tab-presets">
            <Palette size={14} className="mr-1.5" /> Voorinstellingen
          </TabsTrigger>
          <TabsTrigger value="colors" data-testid="settings-tab-colors">
            <Palette size={14} className="mr-1.5" /> Eigen kleuren
          </TabsTrigger>
          <TabsTrigger value="typography" data-testid="settings-tab-typography">
            <TextT size={14} className="mr-1.5" /> Typografie
          </TabsTrigger>
          <TabsTrigger value="layout" data-testid="settings-tab-layout">
            <Rows size={14} className="mr-1.5" /> Indeling
          </TabsTrigger>
        </TabsList>

        <TabsContent value="presets" className="mt-0">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Thema</div>
                <div className="text-slate-100 font-medium">Kies een voorinstelling</div>
              </div>
              <div className="text-xs text-slate-500">Actief: <span className="text-slate-200">{settings.preset === "custom" ? "Custom" : (THEME_PRESETS[settings.preset]?.label || "Custom")}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {Object.entries(THEME_PRESETS).map(([id, preset]) => {
                const active = settings.preset === id;
                const c = preset.colors;
                return (
                  <button
                    key={id}
                    onClick={() => { applyPreset(id); toast.success(`${preset.label} toegepast`); }}
                    data-testid={`preset-${id}`}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      active ? "border-blue-500/60 ring-1 ring-blue-500/40" : "border-slate-800 hover:border-slate-700"
                    }`}
                    style={{ background: c.surface, color: c.text }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold" style={{ color: c.text, fontFamily: "inherit" }}>
                          {preset.label}
                        </div>
                        <div className="text-xs opacity-70 mt-0.5" style={{ color: c.textMuted }}>
                          {preset.description}
                        </div>
                      </div>
                      {active && <CheckCircle weight="fill" size={20} style={{ color: c.primary }} />}
                    </div>
                    <div className="mt-4 flex gap-1.5">
                      {["bg", "surface", "primary", "accent", "text"].map((k) => (
                        <div key={k} className="w-6 h-6 rounded-md border" style={{ background: c[k], borderColor: c.border }} />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex items-center justify-between text-sm">
              <div className="text-slate-500">Je wijzigingen worden automatisch opgeslagen.</div>
              <Button
                variant="outline"
                onClick={() => { resetDefaults(); toast.success("Terug naar Nova standaard"); }}
                className="border-slate-800 bg-slate-900 hover:bg-slate-800 gap-2"
                data-testid="settings-reset"
              >
                <ArrowCounterClockwise size={14} /> Terug naar Nova standaard
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="colors" className="mt-0">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-slate-500">Fijn afstemmen</div>
              <div className="text-slate-100 font-medium">Kies elke kleur</div>
              <div className="text-xs text-slate-500 mt-1">Gebruik de kleurkiezer of typ een HEX-waarde.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {COLOR_KEYS.map(({ key, label }) => (
                <ColorField
                  key={key}
                  label={label}
                  value={settings.colors[key] || "#000000"}
                  onChange={(v) => setColor(key, v)}
                  testid={`color-${key}`}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="typography" className="mt-0 space-y-4">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-slate-500">Lettertype</div>
              <div className="text-slate-100 font-medium">Kies je lettertype</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FONT_OPTIONS.map((f) => {
                const active = settings.fonts?.id === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => { setFontFamily(f.id); toast.success(`Lettertype: ${f.label}`); }}
                    data-testid={`font-${f.id}`}
                    className={`text-left rounded-lg border p-4 transition-colors ${
                      active ? "border-blue-500/60 ring-1 ring-blue-500/40" : "border-slate-800 hover:border-slate-700"
                    }`}
                    style={{ background: "var(--nova-surface)" }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-200 font-medium">{f.label}</div>
                      {active && <CheckCircle weight="fill" size={18} className="text-blue-400" />}
                    </div>
                    <div className="mt-2 text-xl text-slate-100" style={{ fontFamily: `"${f.heading}", sans-serif` }}>
                      Pa\u00EDs verlangt een vrolijke lach
                    </div>
                    <div className="text-sm text-slate-400 mt-1" style={{ fontFamily: `"${f.body}", sans-serif` }}>
                      Broodtekst: springt over de luie hond.
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Tekstgrootte</div>
              <div className="text-slate-100 font-medium">Basisformaat</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {FONT_SIZE_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setFontSize(s.id)}
                  data-testid={`font-size-${s.id}`}
                  className={`rounded-lg border py-3 transition-colors ${
                    settings.font_size === s.id ? "border-blue-500/60 bg-blue-600/10 text-slate-100" : "border-slate-800 text-slate-300 hover:bg-slate-800/40"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="layout" className="mt-0">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Interface-dichtheid</div>
              <div className="text-slate-100 font-medium">Hoeveel ruimte elk element krijgt</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DENSITY_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setDensity(s.id)}
                  data-testid={`density-${s.id}`}
                  className={`rounded-lg border py-3 transition-colors ${
                    settings.density === s.id ? "border-blue-500/60 bg-blue-600/10 text-slate-100" : "border-slate-800 text-slate-300 hover:bg-slate-800/40"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Live preview */}
      <div className="mt-8 rounded-xl border border-slate-800 bg-[#121C2F] p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Live voorbeeld</div>
        <div className="rounded-lg border border-slate-800 p-5 bg-[#0B1120]">
          <div className="text-2xl font-semibold text-slate-50">Dinsdag 5 maart</div>
          <div className="text-sm text-slate-400 mt-1">Je dag in één oogopslag</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { title: "Natuurkundeles", time: "9:00 – 10:30", color: "#F59E0B" },
              { title: "Sport", time: "17:00 – 18:00", color: "#F43F5E" },
              { title: "Hoofdstuk 4 lezen", time: "20:00 – 21:00", color: "#8B5CF6" },
            ].map((it) => (
              <div key={it.title} className="rounded-lg p-3" style={{ background: `${it.color}18`, borderLeft: `3px solid ${it.color}`, border: `1px solid ${it.color}33` }}>
                <div className="text-sm font-medium text-slate-100">{it.title}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{it.time}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Button className="bg-blue-600 hover:bg-blue-500">Hoofdactie</Button>
            <Button variant="outline" className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200">Secundair</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
